/**
 * Orders Normalizer
 * Transforms orders API responses to frontend models.
 * @module api/normalizers/orders
 */

import { safeString, safeNumber, safeObject } from "../safeHelpers"

function createOrderDetailsInvariantError(code, message, details = {}) {
  const error = new Error(`[${code}] ${message}`)
  error.name = "OrderDetailsInvariantError"
  error.code = code
  error.details = details
  return error
}

const OZON_MARKET_STATUS_DISPLAY = Object.freeze({
  awaiting_packaging: "Ожидает сборки",
  new: "Ожидает сборки",
  awaiting_deliver: "Ожидает отправки",
  delivering: "Отправлен",
  delivered: "Доставлен",
  cancelled: "Отменён",
})

/**
 * Normalize a dashboard market-order remote status for display.
 *
 * @param {Object} order - Dashboard order row.
 * @returns {{code: string, label: string, isDelivered: boolean, isCancelled: boolean}}
 * @postcondition Ozon remote status codes render as approved Russian labels; local status `6` is a cancelled fallback when the remote status is missing.
 */
export function normalizeMarketOrderStatusDisplay(order) {
  const remoteCode = safeString(order?.m_item_market_status).trim()
  const isLocallyCancelled = safeNumber(order?.m_item_status) === 6
  const code = remoteCode || (isLocallyCancelled ? "cancelled" : "")
  const isOzon = order?.m_item_market === "ozon"

  return {
    code,
    label: isOzon ? OZON_MARKET_STATUS_DISPLAY[code] || code : code,
    isDelivered: code === "delivered",
    isCancelled: code === "cancelled",
  }
}

function assertNonEmptyOrderProducts(productsArray, orderInfo, source) {
  if (Array.isArray(productsArray) && productsArray.length > 0) {
    return
  }

  const serializedProducts = safeString(orderInfo?.m_item_products)
  const orderId = safeNumber(orderInfo?.m_item_id)

  throw createOrderDetailsInvariantError(
    "ORDER_PRODUCTS_EMPTY",
    `Order ${orderId} has no usable products from ${source}`,
    {
      orderId,
      source,
      serializedProductsLength: serializedProducts.length,
    }
  )
}

// LLM:METADATA
// :hierarchy: [Domain:Orders | Data_Normalization | Service]
// :relates-to: uses: "safeHelpers", required-by: "orders.client.js"
// :rationale: "Transforms backend order responses to match frontend expectations (order structure, field naming, nested objects). Handles both array and object-map product payloads to prevent empty products in dashboard orders."
// :contract: pre: "validated data from orders contract", post: "normalized order response for orders client and OrderRow.vue with products array", invariant: "always returns expected structure, products array is never undefined"
// :decision_cache: "Chose comprehensive normalizer for orders due to complex nested structures (order_info, products, user_stat) and multiple response formats across 14 endpoints. Added product extraction from envelope variants as fallback [ORD-NORM-001, FIX-B]"
// LLM:END

/**
 * Normalize order details response
 * Transforms backend order details to frontend model structure
 *
 * @param {Object} response - Raw order details response from API
 * @param {Object} [response.order_info] - Order information object
 * @param {Array} [response.products] - Order products array
 * @param {Object} [response.sdek_order] - SDEK order info
 * @param {Object} [response.user_stat] - User statistics
 * @returns {Object} Normalized order details
 * @returns {Object} returns.order_info - Normalized order info
 * @returns {Array} returns.products - Normalized products array
 * @returns {Object} returns.params - Alias for order_info (for backward compatibility)
 */
export function normalizeOrderDetailsResponse(response) {
  if (!response) {
    throw createOrderDetailsInvariantError(
      "ORDER_DETAILS_EMPTY_RESPONSE",
      "Order details response is empty"
    )
  }

  // Handle response with order_info field (standard format)
  if (response.order_info) {
    const orderInfo = normalizeOrderInfo(response.order_info)
    let productsArray = normalizeOrderProducts(response.products)

    // Fallback: Extract products from envelope variants if products array is empty
    // This handles the case where backend returns positions in order_info.variants instead of products
    // CRITICAL: Only use variants fallback if products array is truly empty to prevent duplication
    if (
      (!productsArray || productsArray.length === 0) &&
      response.order_info.variants
    ) {
      productsArray = normalizeOrderProducts(response.order_info.variants)
    }

    // CRITICAL FIX: Remove duplicate products by rowid/id to prevent market order duplication
    // This can happen when backend returns both products array and variants with overlapping data
    productsArray = deduplicateProducts(productsArray)

    assertNonEmptyOrderProducts(
      productsArray,
      orderInfo,
      "normalizeOrderDetailsResponse.primary"
    )

    return {
      order_info: orderInfo,
      products: productsArray,
      params: orderInfo,
      sdek_order_id: safeString(response.sdek_order_id),
      sdek_order_print_url: safeString(response.sdek_order_print_url),
      sdek_order_print_id: safeString(response.sdek_order_print_id),
      user_stat: response.user_stat
        ? safeObject(response.user_stat)
        : undefined,
    }
  }

  // Handle array response (legacy format)
  if (Array.isArray(response) && response.length > 0) {
    const first = response[0]
    if (first && typeof first === "object" && first.order_info) {
      const orderInfo = normalizeOrderInfo(first.order_info)
      let productsArray = normalizeOrderProducts(first.products)

      // Fallback: Extract products from envelope variants if products array is empty
      // Only use variants fallback if products array is truly empty
      if (
        (!productsArray || productsArray.length === 0) &&
        first.order_info.variants
      ) {
        productsArray = normalizeOrderProducts(first.order_info.variants)
      }

      // CRITICAL FIX: Remove duplicate products to prevent market order duplication
      productsArray = deduplicateProducts(productsArray)

      assertNonEmptyOrderProducts(
        productsArray,
        orderInfo,
        "normalizeOrderDetailsResponse.legacy_array"
      )

      return {
        order_info: orderInfo,
        products: productsArray,
        params: orderInfo,
        sdek_order_id: safeString(first.sdek_order_id),
        sdek_order_print_url: safeString(first.sdek_order_print_url),
        sdek_order_print_id: safeString(first.sdek_order_print_id),
        user_stat: first.user_stat ? safeObject(first.user_stat) : undefined,
      }
    }
  }

  // Fallback: treat response as order_info directly
  const fallbackOrderInfo = normalizeOrderInfo(response.order_info || response)
  let fallbackProducts = normalizeOrderProducts(response.products)

  // Last resort: Try to extract from variants in the fallback response
  // Only use if products array is truly empty
  if (
    (!fallbackProducts || fallbackProducts.length === 0) &&
    response.variants
  ) {
    fallbackProducts = normalizeOrderProducts(response.variants)
  }

  // CRITICAL FIX: Remove duplicate products
  fallbackProducts = deduplicateProducts(fallbackProducts)

  assertNonEmptyOrderProducts(
    fallbackProducts,
    fallbackOrderInfo,
    "normalizeOrderDetailsResponse.fallback"
  )

  return {
    order_info: fallbackOrderInfo,
    products: fallbackProducts,
    params: fallbackOrderInfo,
  }
}

/**
 * Normalize order products from array/object backend formats
 *
 * @param {Array|Object} products - Raw products list
 * @returns {Array} Normalized products array
 */
function normalizeOrderProducts(products) {
  if (Array.isArray(products)) {
    return products
      .filter((product) => isUsableOrderProduct(product))
      .map(normalizeOrderProduct)
  }

  // LLM:METADATA
  // :topology: [Domain:Orders | Normalization | ProductExtraction]
  // :relates-to: uses: "products object keys as rowid", required-by: "normalizeOrderDetailsResponse"
  // :rationale: "Preserve rowid from object-map keys when backend returns products as object instead of array. Object.keys hold the rowid values that must be attached to each product for proper delete operations in OrderRow."
  // :contract: pre: "products is object with rowid keys", post: "Each product has rowid field from object key", invariant: "rowid is preserved from source structure"
  // :decision_cache: "Changed from Object.values to Object.entries to preserve rowid context [FIX-ROWID-001]"
  // LLM:END
  if (products && typeof products === "object") {
    return Object.entries(products)
      .filter(([rowid, product]) => isUsableOrderProduct(product, rowid))
      .map(([rowid, product]) => ({
        ...normalizeOrderProduct(product),
        rowid,
      }))
  }

  return []
}

function isUsableOrderProduct(product, sourceRowid = null) {
  if (!product || typeof product !== "object" || Array.isArray(product)) {
    return false
  }

  const options = safeObject(product.options)
  const id = product.id ?? product.m_item_id
  const rowid = product.rowid ?? sourceRowid
  const hasIdentity =
    (id !== undefined && id !== null && String(id).trim() !== "") ||
    (rowid !== undefined && rowid !== null && String(rowid).trim() !== "")
  const hasDeclaredShape =
    safeString(product.product_type).trim() !== "" ||
    safeString(product.name || product.m_item_title).trim() !== "" ||
    Object.keys(options).length > 0

  return hasIdentity || hasDeclaredShape
}

/**
 * Remove duplicate products from array.
 * Uses an explicit backend rowid as the primary identity. Legacy products with
 * no rowid use their canonical product type, id, and external media group.
 *
 * @param {Array} products - Products array (may contain duplicates)
 * @returns {Array} Deduplicated products array
 */
function deduplicateProducts(products) {
  if (!Array.isArray(products) || products.length === 0) {
    return products
  }

  const seenRowids = new Set()
  const seenLegacyIdentities = new Set()
  const deduplicated = []

  for (const product of products) {
    const hasExplicitRowid =
      product.rowid !== undefined &&
      product.rowid !== null &&
      product.rowid !== ""

    if (hasExplicitRowid) {
      const rowidIdentity = String(product.rowid)
      if (!seenRowids.has(rowidIdentity)) {
        seenRowids.add(rowidIdentity)
        deduplicated.push(product)
      }
      continue
    }

    const legacyIdentity = JSON.stringify([
      product.product_type,
      product.id,
      product.product_type === "ext_catalog"
        ? (product.options?.media_group ?? "")
        : null,
    ])

    if (!seenLegacyIdentities.has(legacyIdentity)) {
      seenLegacyIdentities.add(legacyIdentity)
      deduplicated.push(product)
    }
  }

  return deduplicated
}

/**
 * Normalize order info object
 *
 * @param {Object} orderInfo - Raw order info
 * @returns {Object} Normalized order info
 */
function normalizeOrderInfo(orderInfo) {
  if (!orderInfo || typeof orderInfo !== "object") {
    return {}
  }

  return {
    ...safeObject(orderInfo),
    m_item_id: safeNumber(orderInfo.m_item_id),
    m_item_status: safeNumber(orderInfo.m_item_status),
    m_item_paid: safeNumber(orderInfo.m_item_paid),
    m_item_payment_type: safeNumber(orderInfo.m_item_payment_type),
    m_item_admin_comment: safeString(orderInfo.m_item_admin_comment),
    m_item_discount_fixed: safeNumber(orderInfo.m_item_discount_fixed),
    m_item_discount_percent: safeNumber(orderInfo.m_item_discount_percent),
    m_item_cert_discount: safeNumber(orderInfo.m_item_cert_discount),
    m_item_feedback_check:
      orderInfo.m_item_feedback_check === true ||
      orderInfo.m_item_feedback_check === 1 ||
      orderInfo.m_item_feedback_check === "1",
    m_item_client_name: safeString(orderInfo.m_item_client_name),
    m_item_client_email: safeString(orderInfo.m_item_client_email),
    m_item_client_phone: safeString(orderInfo.m_item_client_phone),
    m_item_delivery_type: safeNumber(orderInfo.m_item_delivery_type),
    m_item_delivery_price: safeNumber(orderInfo.m_item_delivery_price),
    m_item_bertus_delivery_price: safeNumber(
      orderInfo.m_item_bertus_delivery_price
    ),
    m_item_bertus_delivery_type: safeNumber(
      orderInfo.m_item_bertus_delivery_type
    ),
    m_item_delivery_ins: safeString(orderInfo.m_item_delivery_ins),
    m_item_delivery_pvz_address: safeString(
      orderInfo.m_item_delivery_pvz_address
    ),
    m_item_postcode: safeString(orderInfo.m_item_postcode),
    m_item_city: safeString(orderInfo.m_item_city),
    m_item_address: safeString(orderInfo.m_item_address),
    m_item_country: safeString(orderInfo.m_item_country),
    m_item_delivery_comment: safeString(orderInfo.m_item_delivery_comment),
    m_item_delivery_more_data: safeString(orderInfo.m_item_delivery_more_data),
    m_item_for_gift: safeNumber(orderInfo.m_item_for_gift),
    m_item_for_gift_comment: safeString(orderInfo.m_item_for_gift_comment),
    m_item_cert_text: safeString(orderInfo.m_item_cert_text),
    m_item_user: safeString(orderInfo.m_item_user),
    m_item_url_ref: safeString(orderInfo.m_item_url_ref),
    m_item_oracle_manager: safeString(orderInfo.m_item_oracle_manager),
    m_item_promocode_name: safeString(orderInfo.m_item_promocode_name),
    m_item_tg_name: safeString(orderInfo.m_item_tg_name),
  }
}

/**
 * Normalize order product
 *
 * @param {Object} product - Raw product object
 * @returns {Object} Normalized product
 */
function normalizeOrderProduct(product) {
  if (!product || typeof product !== "object") {
    return {}
  }

  const options = safeObject(product.options)
  const id = product.id ?? product.m_item_id
  const price =
    product.price ?? product.m_item_price ?? product.m_item_sale_price ?? 0
  const qty = product.qty ?? 1
  const name = product.name ?? product.m_item_title

  return {
    ...safeObject(product),
    id: safeString(id),
    qty,
    price,
    name: safeString(name),
    options,
    supplier: safeString(product.supplier || product.m_item_supplier),
    product_type: safeString(product.product_type).trim(),
    artist_name: safeString(product.artist_name || options.artist_name),
    m_item_title: safeString(product.m_item_title || product.name),
    m_item_album_tail: safeString(product.m_item_album_tail),
    m_item_supplier: safeString(product.m_item_supplier || product.supplier),
    m_item_price: safeNumber(product.m_item_price || product.price),
    m_item_sale_price: safeNumber(product.m_item_sale_price),
    linked_position_only:
      product.linked_position_only === true ||
      product.linked_position_only === 1 ||
      product.linked_position_only === "1" ||
      product.linked_position_only === "true",
  }
}

/**
 * Normalize update order params response
 *
 * @param {Object} response - Raw update response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.message] - Response message
 */
export function normalizeUpdateOrderParamsResponse(response) {
  if (!response) {
    return {
      success: false,
      message: "No response from server",
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
    message: safeString(response.message),
  }
}

/**
 * Normalize assign manager response
 *
 * @param {Object} response - Raw assign manager response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 */
export function normalizeAssignManagerResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
  }
}

/**
 * Normalize message template response
 *
 * @param {Object} response - Raw message template response
 * @returns {Object} Normalized response
 * @returns {string} returns.message - Message template
 */
export function normalizeMessageTemplateResponse(response) {
  if (!response) {
    return {
      message: "",
    }
  }

  return {
    message: safeString(response.message),
  }
}

/**
 * Normalize send message response
 *
 * @param {Object} response - Raw send message response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 */
export function normalizeSendMessageResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
  }
}

/**
 * Normalize add tracking number response
 *
 * @param {Object} response - Raw tracking number response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.error] - Error message if failed
 */
export function normalizeAddTrackingNumberResponse(response) {
  if (!response) {
    return {
      success: false,
      error: "No response from server",
    }
  }

  return {
    success: !response.errors,
    error: response.errors ? safeString(response.errors) : undefined,
  }
}

/**
 * Normalize print CDEK label response
 *
 * @param {Object} response - Raw CDEK print response
 * @returns {Object} Normalized response
 * @returns {string} returns.url - Print URL
 * @returns {string} returns.wait - Wait message if not ready
 */
export function normalizePrintCDEKLabelResponse(response) {
  if (!response) {
    return {
      url: "",
      wait: "",
      print_id: "",
    }
  }

  return {
    url: safeString(response.url),
    wait: safeString(response.wait),
    print_id: safeString(response.print_id),
  }
}

/**
 * Normalize delivery price response
 *
 * @param {Object} response - Raw delivery price response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 * @returns {string} [returns.error] - Error message if failed
 */
export function normalizeDeliveryPriceQuoteResponse(response) {
  if (!response) {
    return {
      price: 0,
    }
  }

  return {
    price: safeNumber(response.price),
  }
}

export function normalizeDeliveryPriceSaveResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success: safeNumber(response.status) === 1,
  }
}

/**
 * Normalize add product response
 *
 * @param {Object} response - Raw add product response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 */
export function normalizeAddProductResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success:
      response.result === "ok" ||
      response.result === "success" ||
      response.result === 1,
    new_sum:
      response.new_sum === undefined ? undefined : safeNumber(response.new_sum),
    new_full_sum:
      response.new_full_sum === undefined
        ? undefined
        : safeNumber(response.new_full_sum),
    delivery_price:
      response.delivery_price === undefined
        ? undefined
        : safeNumber(response.delivery_price),
  }
}

/**
 * Normalize apply promocode response
 *
 * @param {Object} response - Raw promocode response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 */
export function normalizeApplyPromocodeResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
  }
}

/**
 * Normalize oracle recommendations response
 *
 * @param {Object} response - Raw oracle response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 */
export function normalizeOracleRecommendationsResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success:
      response.result === 1 ||
      response.result === "ok" ||
      response.result === "success",
  }
}

/**
 * Normalize save order ready status response
 * Used for market orders to mark order as ready
 *
 * @param {Object} response - Raw save ready status response
 * @returns {Object} Normalized response
 * @returns {boolean} returns.success - Success indicator
 */
export function normalizeSaveOrderReadyStatusResponse(response) {
  if (!response) {
    return {
      success: false,
    }
  }

  return {
    success: response.result === "ok" || response.result === "success",
  }
}
